/**
 * Internal module: lazily-initialized Firebase app + Auth singleton.
 * Not exported from the package's public surface.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { UserAccessError, type FirebaseWebConfig, type UserAccessOptions } from './types';

let pendingConfig: FirebaseWebConfig | null = null;
let pendingOptions: UserAccessOptions = {};
let app: FirebaseApp | null = null;

/**
 * Wire up the Firebase web app configuration. Must be called once at app boot
 * before any of the imperative auth functions (or the hooks). Creating the
 * Firebase app itself is deferred until the first `getAuthInstance()` call so
 * tests and tree-shaken consumers don't pay for unused init.
 *
 * `options` carries our own behaviour, kept out of `FirebaseWebConfig` so that
 * type stays a faithful description of the Firebase web app config.
 */
export function initUserAccess(config: FirebaseWebConfig, options: UserAccessOptions = {}): void {
  pendingConfig = config;
  pendingOptions = options;
  app = null;
}

/**
 * @internal — for tests that need to reset global state between cases.
 */
export function __resetUserAccess(): void {
  pendingConfig = null;
  pendingOptions = {};
  app = null;
}

/**
 * @internal — read by the email senders in `login.ts`.
 *
 * Deliberately app-wide rather than a per-call argument: a call site that has
 * to *remember* to pass the URL silently falls back to the project default when
 * it forgets, and the symptom (a stranger-looking link in an email nobody on the
 * team receives) is close to undetectable.
 */
export function getContinueUrl(): string | undefined {
  return pendingOptions.continueUrl;
}

function ensureApp(): FirebaseApp {
  if (app) return app;
  if (!pendingConfig) {
    throw new UserAccessError(
      'not-initialized',
      'User access not initialized. Call initUserAccess(firebaseConfig) at app startup.'
    );
  }
  const apiKey = pendingConfig.apiKey?.trim();
  const projectId = pendingConfig.projectId?.trim();
  if (!apiKey || !projectId) {
    throw new UserAccessError(
      'not-initialized',
      'Firebase config missing apiKey or projectId. Set VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID (plus the other standard web app keys) in your environment.'
    );
  }
  app = initializeApp({
    apiKey,
    authDomain: pendingConfig.authDomain,
    projectId,
    storageBucket: pendingConfig.storageBucket,
    messagingSenderId: pendingConfig.messagingSenderId,
    appId: pendingConfig.appId,
  });
  return app;
}

export function getAuthInstance(): Auth {
  return getAuth(ensureApp());
}
